import moment from "moment";
import React, { ReactElement, useEffect, useState } from "react";
import Avatar from "react-avatar";
import {
	ProjectAttachmentsDropzone,
	ProjectBoard,
	ProjectMember,
	useProjects,
} from ".";
import { LeadSelect } from "../leads";
import { Editable, Loading } from "../ui";
import { UserSelect, useUsers } from "../users";

import {
	faBuilding,
	faCalendarAlt,
	faClock,
	faDollarSign,
	faDownload,
	faEye,
	faFolder,
	faHourglass,
	faTasks,
	faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	Button,
	FormControl,
	FormGroup,
	Tab,
	Tabs,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { RouteComponentProps } from "react-router";
import { toast } from "react-toastify";
import { AttachmentsListGroup } from "../attachments";

interface IParams {
	id?: string;
}

export const ProjectDashboardPage = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const projectContext = useProjects();
	const userContext = useUsers();

	const [attachmentsTabKey, setAttachmentsTabKey] = useState("global");

	useEffect(() => {
		projectContext.find!(parseInt(props.match.params.id || "", 10));

		return function cleanup() {
			projectContext.setProject!(null);
		};
	}, []);

	if (!projectContext.project) {
		return <Loading />;
	}

	const project = projectContext.project;

	document.title = `Project Dashboard - ${project.name}`;

	const handleClickWatch = async (event: React.MouseEvent) => {
		event.stopPropagation();
		const proj = await projectContext.toggleWatchingProject!();

		if (userContext.user) {
			if (proj && proj.isWatchedBy(userContext.user)) {
				toast(
					"You are now watching this project, you will be notified when comments or changes are made to any task in this project.",
				);
			} else {
				toast("You are no longer watching this project.");
			}
		}
	};

	const titleField = (
		<Editable
			shownComponent={
				<h1 className="project-name">{project.name}</h1>
			}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={projectContext.project.name}
						onChange={(event: any) => {
							project.name = event.target.value;
						}}
						className="project-name-input"
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const budgetField = (
		<Editable
			shownComponent={<span className="stats-value">${project.budget}</span>}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={String(project.budget)}
						onChange={(event: any) => {
							project.budget = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const estimatedHoursField = (
		<Editable
			shownComponent={<span className="stats-value">{project.estimatedHours}</span>}
			editComponent={
				<FormGroup>
					<FormControl
						type="number"
						defaultValue={String(project.estimatedHours)}
						onChange={(event: any) => {
							project.estimatedHours = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const projectGroupField = (
		<Editable
			shownComponent={
				project.group ? (
					<span className="project-badge group-badge">
						<FontAwesomeIcon icon={faFolder} />
						{project.group}
					</span>
				) : (
					<span className="project-badge group-badge add-group">
						<FontAwesomeIcon icon={faFolder} />
						Add Group
					</span>
				)
			}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={project.group}
						onChange={(event: any) => {
							project.group = event.target.value;
						}}
						placeholder="Enter group name"
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const getStatusClass = (status: string) => {
		switch (status?.toLowerCase()) {
			case "completed":
				return "status-completed";
			case "in progress":
				return "status-in-progress";
			case "on hold":
				return "status-on-hold";
			case "not started":
				return "status-not-started";
			default:
				return "status-default";
		}
	};

	const projectStatusField = (
		<Editable
			shownComponent={
				<span className={`project-badge ${getStatusClass(project.status)}`}>
					{project.status}
				</span>
			}
			editComponent={
				<FormGroup>
					<FormControl
						as="select"
						type="test"
						defaultValue={project.status}
						onChange={(event: any) => {
							project.status = event.target.value;
						}}
					>
						<option value="Not Started">Not Started</option>
						<option value="In Progress">In Progress</option>
						<option value="On Hold">On Hold</option>
						<option value="Internal">Internal</option>
						<option value="Support">Support</option>
						<option value="Completed">Completed</option>
					</FormControl>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const dueDateField = (
		<Editable
			shownComponent={
				<span className="stats-value">
					{project.endDate
						? moment(project.endDate).format("MMM D, YYYY")
						: "Not Set"}
				</span>
			}
			editComponent={
				<FormGroup>
					<DatePicker
						className="form-control"
						selected={project.endDate}
						onChange={(date: Date | any) => {
							project.endDate = date;
							projectContext.save!();
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const startDateField = (
		<Editable
			shownComponent={
				<span className="stats-value">
					{project.startDate
						? moment(project.startDate).format("MMM D, YYYY")
						: "Not Set"}
				</span>
			}
			editComponent={
				<FormGroup>
					<DatePicker
						className="form-control"
						selected={project.startDate}
						onChange={(date: Date | null) => {
							project.startDate = date;
							projectContext.save!();
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const memberAvatars = project.members.map(
		(member: ProjectMember, index: number): any => {
			if (member.user) {
				return (
					<Avatar
						round
						key={index}
						name={member.user ? member.user.fullName : "NA"}
						size="32"
						color="#e0f2fe"
						fgColor="#0891b2"
						textSizeRatio={2}
						style={{ marginRight: 4 }}
					/>
				);
			}
		},
	);

	const memberEditField = (
		<Editable
			shownComponent={
				<div className="avatar-group">
					{memberAvatars}
					<Avatar
						round
						name="+"
						size="32"
						color="#e0f2fe"
						fgColor="#0891b2"
						textSizeRatio={2}
					/>
				</div>
			}
			editComponent={
				<FormGroup>
					<UserSelect
						defaultValue={project.members.map(
							(member: ProjectMember) => {
								if (member.user) {
									return {
										value: member.user.id,
										label: member.user.fullName,
									};
								}
								return null;
							},
						)}
						handleChange={(_selected, type) => {
							let option = null;
							if (type.action === "remove-value") {
								option = type.removedValue;
							} else {
								option = type.option;
							}

							projectContext.saveMembers!(
								type.action,
								option.value,
								"user",
							);
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const customerAvatars = project.members.map(
		(member: ProjectMember, index: number): any => {
			if (member.lead) {
				return (
					<Avatar
						round
						key={index}
						name={member.lead ? member.lead.fullName : "NA"}
						size="32"
						color="#e0f2fe"
						fgColor="#0891b2"
						textSizeRatio={2}
						style={{ marginRight: 4 }}
					/>
				);
			}
		},
	);

	const customerEditField = (
		<Editable
			shownComponent={
				<div className="avatar-group">
					{customerAvatars}
					<Avatar
						round
						name="+"
						size="32"
						color="#e0f2fe"
						fgColor="#0891b2"
						textSizeRatio={2}
					/>
				</div>
			}
			editComponent={
				<FormGroup>
					<LeadSelect
						accountId={project.accountId}
						defaultValue={project.members.map(
							(member: ProjectMember) => {
								if (member.lead) {
									return {
										value: member.lead.id,
										label: member.lead.fullName,
									};
								}
								return null;
							},
						)}
						handleChange={(_selected, type) => {
							let option = null;
							if (type.action === "remove-value") {
								option = type.removedValue;
							} else {
								option = type.option;
							}

							projectContext.saveMembers!(
								type.action,
								option.value,
								"lead",
							);
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	return (
		<div id="project-dashboard">
			{/* Header Card */}
			<div className="project-header-card">
				<div className="project-header-main">
					<div className="project-icon-wrapper">
						<FontAwesomeIcon icon={faTasks} />
					</div>
					<div className="project-info">
						{titleField}
						<div className="project-meta">
							{projectStatusField}
							{projectGroupField}
							<button
								className={`project-watch-btn ${
									userContext.user && project.isWatchedBy(userContext.user)
										? "watching"
										: ""
								}`}
								onClick={handleClickWatch}
							>
								<FontAwesomeIcon icon={faEye} />
								{userContext.user && project.isWatchedBy(userContext.user)
									? "Watching"
									: "Watch"}
							</button>
						</div>
					</div>
				</div>
				<div className="project-header-actions">
					<a href={`/accounts/${project.accountId}`} className="project-account-link">
						<FontAwesomeIcon icon={faBuilding} />
						View Account
					</a>
				</div>
			</div>

			{/* Stats Grid */}
			<div className="project-stats-grid">
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faCalendarAlt} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Start Date</span>
						{startDateField}
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faCalendarAlt} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Due Date</span>
						{dueDateField}
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faDollarSign} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Budget</span>
						{budgetField}
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faHourglass} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Time Allocated</span>
						{estimatedHoursField}
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faClock} />
					</div>
					<div className="stats-content">
						<span className="stats-label">
							Time Logged
							<a href={project.timelogDownloadLink} className="download-link">
								<FontAwesomeIcon icon={faDownload} />
							</a>
						</span>
						<span className="stats-value">{project.timeLogged}</span>
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faUsers} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Team</span>
						{memberEditField}
					</div>
				</div>
				<div className="project-stats-card">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faUsers} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Client</span>
						{customerEditField}
					</div>
				</div>
			</div>

			{/* Content Grid */}
			<div className="project-content-grid">
				{/* Opportunities & Budgets Card */}
				{(project.opportunities.length > 0 || project.budgets.length > 0) && (
					<div className="project-content-card">
						<div className="project-content-header">
							<FontAwesomeIcon icon={faDollarSign} />
							Opportunities & Budgets
						</div>
						<div className="project-content-body">
							{project.opportunities.length > 0 && (
								<>
									<div className="content-section-title">Opportunities</div>
									<ul className="project-list">
										{project.opportunities.map((opp, index) => (
											<li key={index}>
												<a href={`/opportunities/${opp.id}`}>
													{opp.name}
													<span className="amount">${opp.amount.toFixed(2)}</span>
												</a>
											</li>
										))}
									</ul>
								</>
							)}
							{project.budgets.length > 0 && (
								<>
									<div className="content-section-title">Budgets</div>
									<ul className="project-list">
										{project.budgets.map((budget, index) => (
											<li key={index}>
												<a href={`/project-budget/${budget.id}`}>
													#{budget.id} {budget.name}
													<span className="amount">${budget.amount}</span>
												</a>
											</li>
										))}
									</ul>
								</>
							)}
						</div>
					</div>
				)}

				{/* Files Card */}
				<div className="project-files-card">
					<div className="project-content-header">
						<FontAwesomeIcon icon={faFolder} />
						Files & Notes
					</div>
					<div className="project-content-body">
						<Tabs
							id="files-tab"
							activeKey={attachmentsTabKey}
							onSelect={(k: any) => {
								setAttachmentsTabKey(k);
							}}
							className="project-tabs"
						>
							<Tab eventKey="global" title="Project Files">
								<div className="tab-content-wrapper">
									<ProjectAttachmentsDropzone />
								</div>
							</Tab>
							<Tab eventKey="task" title="Task Files">
								<div className="tab-content-wrapper">
									<AttachmentsListGroup
										attachments={project.taskAttachments}
										folder={`projects/${project.id}/tasks`}
										appendAttachableId={true}
									/>
								</div>
							</Tab>
							<Tab eventKey="notes" title="Notes">
								<div className="tab-content-wrapper">
									<FormGroup>
										<FormControl
											as="textarea"
											type="text"
											defaultValue={projectContext.project.notes}
											onChange={(event: any) => {
												project.notes = event.target.value;
											}}
											className="notes-textarea"
										/>
									</FormGroup>
									<Button
										variant="primary"
										className="save-notes-btn"
										onClick={() => {
											projectContext.save!();
											toast("Notes have been saved.");
										}}
									>
										Save Notes
									</Button>
								</div>
							</Tab>
						</Tabs>
					</div>
				</div>
			</div>

			{/* Task Board Section */}
			<div className="project-content-card task-board-card">
				<div className="project-content-header">
					<FontAwesomeIcon icon={faTasks} />
					Task Board
					<a href={project.tasksDownloadLink} className="header-action-link">
						<FontAwesomeIcon icon={faDownload} />
						Export
					</a>
				</div>
				<div className="project-content-body task-board-body">
					<ProjectBoard project={project} />
				</div>
			</div>
		</div>
	);
};
